import { useEffect, useState, useRef } from "react";
import { http } from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

function diffPct(now, before) {
  if (!before) return null;
  return Math.round(((now - before) / before) * 100);
}

function Pill({ label, value, sub, delta }) {
  return (
    <div className="border-2 border-ink bg-paper p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">{label}</p>
      <p className="font-serif-display text-5xl mt-2 text-ink leading-none">
        {value?.toLocaleString("es-MX") ?? "—"}
      </p>
      <div className="mt-2 flex items-baseline gap-2">
        {sub && <span className="text-[11px] text-sub font-mono uppercase tracking-widest">{sub}</span>}
        {delta != null && (
          <span className={`font-mono text-[11px] ${delta >= 0 ? "text-verde" : "text-rojo"}`}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}% vs ayer
          </span>
        )}
      </div>
    </div>
  );
}

export default function TrafficTodayTab() {
  const [data, setData] = useState(null);
  const [auto, setAuto] = useState(true);
  const timerRef = useRef(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get("/admin/stats");
      setData(data);
      setUpdatedAt(new Date());
    } catch {
      /* keep last value */
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!auto) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(load, 30000); // 30s
    return () => clearInterval(timerRef.current);
  }, [auto]);

  if (!data) {
    return <p className="font-mono text-sm text-sub" data-testid="traffic-loading">Cargando tráfico…</p>;
  }

  const dTotal = diffPct(data.today_total, data.yesterday_total);
  const dUnique = diffPct(data.today_unique, data.yesterday_unique);
  const peak = (data.by_hour_today || []).reduce(
    (acc, h) => (h.count > (acc?.count || 0) ? h : acc), null
  );

  return (
    <div data-testid="admin-traffic-tab" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-3xl">Tráfico hoy</h3>
          <p className="text-sm text-sub mt-1 font-mono">
            CDMX · {data.now_cdmx} · {auto ? "actualizando cada 30 s" : "actualización manual"}
            {updatedAt && <> · última lectura {updatedAt.toLocaleTimeString("es-MX")}</>}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAuto((a) => !a)}
            className={`border border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
              auto ? "bg-verde text-paper border-verde" : "hover:bg-ink hover:text-paper"
            }`}
            data-testid="traffic-auto"
          >
            {auto ? "● En vivo" : "○ Pausado"}
          </button>
          <button
            onClick={load}
            className="border border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
            data-testid="traffic-refresh"
          >
            ↻ Actualizar
          </button>
        </div>
      </div>

      {/* Hero KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Pill
          label="Visitas hoy"
          value={data.today_total}
          sub={`ayer ${data.yesterday_total?.toLocaleString("es-MX") || 0}`}
          delta={dTotal}
        />
        <Pill
          label="Únicos hoy"
          value={data.today_unique}
          sub={`ayer ${data.yesterday_unique?.toLocaleString("es-MX") || 0}`}
          delta={dUnique}
        />
        <Pill
          label="Pico del día"
          value={peak?.count || 0}
          sub={peak ? `a las ${peak.label}` : "—"}
        />
        <Pill
          label="Suscriptores"
          value={data.subscribers_total}
          sub="acumulados"
        />
      </div>

      {/* Hourly chart */}
      <div className="border-2 border-ink bg-paper p-4" data-testid="traffic-hour-chart">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub mb-2">Visitas por hora · hoy (CDMX)</p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.by_hour_today} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#0A0A0A22" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }} interval={2} />
            <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <Tooltip
              contentStyle={{ background: "#F4F1EB", border: "1px solid #0A0A0A", fontFamily: "monospace", fontSize: 11 }}
              labelFormatter={(v) => `Hora ${v}`}
              formatter={(v) => [v, "visitas"]}
            />
            <Bar dataKey="count" fill="#0A0A0A" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* 14-day trend */}
      <div className="border border-ink bg-paper p-4" data-testid="traffic-14d-chart">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub mb-2">Visitas diarias · últimos 14 días</p>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data.by_day} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#0A0A0A22" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <Tooltip
              contentStyle={{ background: "#F4F1EB", border: "1px solid #0A0A0A", fontFamily: "monospace", fontSize: 11 }}
              formatter={(v) => [v, "visitas"]}
            />
            <Line type="monotone" dataKey="count" stroke="#8B0000" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Top breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-ink bg-paper">
          <div className="px-3 py-2 border-b border-ink bg-ink text-paper">
            <p className="font-mono text-[11px] uppercase tracking-widest">Top países (7d)</p>
          </div>
          <ul className="divide-y divide-rule">
            {(data.by_country || []).slice(0, 8).map((c, i) => (
              <li key={i} className="flex items-baseline justify-between px-3 py-2 text-sm">
                <span className="truncate">{c.country}</span>
                <span className="font-mono">{c.count.toLocaleString("es-MX")}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-ink bg-paper">
          <div className="px-3 py-2 border-b border-ink bg-ink text-paper">
            <p className="font-mono text-[11px] uppercase tracking-widest">Top páginas (7d)</p>
          </div>
          <ul className="divide-y divide-rule">
            {(data.by_path || []).slice(0, 8).map((p, i) => (
              <li key={i} className="flex items-baseline justify-between px-3 py-2 text-sm">
                <span className="truncate max-w-[70%]" title={p.path}>{p.path}</span>
                <span className="font-mono">{p.count.toLocaleString("es-MX")}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-ink bg-paper">
          <div className="px-3 py-2 border-b border-ink bg-ink text-paper">
            <p className="font-mono text-[11px] uppercase tracking-widest">Top referrers (7d)</p>
          </div>
          <ul className="divide-y divide-rule">
            {((data.by_referrer || []).length === 0)
              ? <li className="px-3 py-2 text-sm text-sub">Sin referrers en 7d.</li>
              : data.by_referrer.slice(0, 8).map((r, i) => (
                <li key={i} className="flex items-baseline justify-between px-3 py-2 text-sm">
                  <span className="truncate max-w-[70%]" title={r.referrer}>{r.referrer}</span>
                  <span className="font-mono">{r.count.toLocaleString("es-MX")}</span>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
